import TileIcon from "@entrilia/shared/icons/TileIcon.tsx";
import CalendarViewMonthOutlinedIcon from "@mui/icons-material/CalendarViewMonthOutlined";
import { Stack } from "@mui/material";
import { useSelector } from "react-redux";
import { ReportType } from "../../../../../../../api/types/svTypes.ts";
import useClientContext from "../../../../../../../context/useClientContext.ts";
import { useSupportedReportTypesContext } from "../../../contexts/SupportedReportTypesContext.ts";
import { selectCurrentDataSetId } from "../../../store/selectors/metaDataSelectors.ts";
import { DataSetIds } from "../../../utilities/DimensionNames.ts";
import { useVisualizationContext } from "./context/VisualizationContext";
import AreaChartIcon from "./icons/AreaChartIcon.tsx";
import BarChartIcon from "./icons/BarChartIcon.tsx";
import DoughnutChartIcon from "./icons/DoughnutChartIcon.tsx";
import FinancialStatementReportIcon from "./icons/FinancialStatementReportIcon.tsx";
import IrrDetailsIcon from "./icons/IrrDetailsIcon.tsx";
import LineChartIcon from "./icons/LineChartIcon.tsx";
import PieChartIcon from "./icons/PieChartIcon.tsx";
import PivotIcon from "./icons/PivotIcon.tsx";
import TabularIcon from "./icons/TabularIcon.tsx";
import ReportTypeBadge from "./ReportTypeBadge";

const ReportTypesContainer = () => {
  const { isPortalFeatureEnabled } = useClientContext();
  const { isReportChanged, onReportTypeClicked, report, editDisabled } = useVisualizationContext();
  const selectedReportType = report.reportType || ReportType.Tabular;
  const { supportedReportTypes } = useSupportedReportTypesContext();
  const dataSetId = useSelector(selectCurrentDataSetId);

  const isSupportedReportType = (reportType: ReportType) => {
    return supportedReportTypes.includes(reportType);
  };

  return (
    <Stack direction="row">
      {isSupportedReportType(ReportType.Tabular) && (
        <ReportTypeBadge
          reportType={ReportType.Tabular}
          isReportChanged={isReportChanged}
          onSelected={onReportTypeClicked}
          tooltipText="Tabular Report"
          ReactTypeIcon={TabularIcon}
          selectedReportType={selectedReportType}
          editDisabled={editDisabled}
        />
      )}
      {isSupportedReportType(ReportType.Pivot) && (
        <ReportTypeBadge
          reportType={ReportType.Pivot}
          isReportChanged={isReportChanged}
          onSelected={onReportTypeClicked}
          tooltipText="Pivot Report"
          ReactTypeIcon={PivotIcon}
          selectedReportType={selectedReportType}
          editDisabled={editDisabled}
        />
      )}
      {isSupportedReportType(ReportType.List) && isPortalFeatureEnabled("SmartViewsListReportsEnabled") && (
        <ReportTypeBadge
          reportType={ReportType.List}
          isReportChanged={isReportChanged}
          onSelected={onReportTypeClicked}
          tooltipText="List Report"
          ReactTypeIcon={CalendarViewMonthOutlinedIcon}
          selectedReportType={selectedReportType}
          editDisabled={editDisabled}
        />
      )}
      {isSupportedReportType(ReportType.FinancialStatement) &&
        isPortalFeatureEnabled("SmartViewsFinancialStatementsEnabled") &&
        dataSetId === DataSetIds.GeneralAndMemo && (
          <ReportTypeBadge
            reportType={ReportType.FinancialStatement}
            isReportChanged={isReportChanged}
            onSelected={onReportTypeClicked}
            tooltipText="Financial Statement Report"
            ReactTypeIcon={FinancialStatementReportIcon}
            selectedReportType={selectedReportType}
            editDisabled={editDisabled}
          />
        )}
      {isSupportedReportType(ReportType.CashFlow) && (
        <ReportTypeBadge
          reportType={ReportType.CashFlow}
          isReportChanged={isReportChanged}
          onSelected={onReportTypeClicked}
          tooltipText="IRR Details Report"
          ReactTypeIcon={IrrDetailsIcon}
          selectedReportType={selectedReportType}
          editDisabled={editDisabled}
        />
      )}
      {isSupportedReportType(ReportType.BarChart) && (
        <ReportTypeBadge
          reportType={ReportType.BarChart}
          isReportChanged={isReportChanged}
          onSelected={onReportTypeClicked}
          tooltipText="Bar Chart Report"
          ReactTypeIcon={BarChartIcon}
          selectedReportType={selectedReportType}
          editDisabled={editDisabled}
        />
      )}
      {isSupportedReportType(ReportType.LineChart) && (
        <ReportTypeBadge
          reportType={ReportType.LineChart}
          isReportChanged={isReportChanged}
          onSelected={onReportTypeClicked}
          tooltipText="Line Chart Report"
          ReactTypeIcon={LineChartIcon}
          selectedReportType={selectedReportType}
          editDisabled={editDisabled}
        />
      )}
      {isSupportedReportType(ReportType.AreaChart) && (
        <ReportTypeBadge
          reportType={ReportType.AreaChart}
          isReportChanged={isReportChanged}
          onSelected={onReportTypeClicked}
          tooltipText="Area Chart Report"
          ReactTypeIcon={AreaChartIcon}
          selectedReportType={selectedReportType}
          editDisabled={editDisabled}
        />
      )}
      {isSupportedReportType(ReportType.PieChart) && (
        <ReportTypeBadge
          reportType={ReportType.PieChart}
          isReportChanged={isReportChanged}
          onSelected={onReportTypeClicked}
          tooltipText="Pie Chart Report"
          ReactTypeIcon={PieChartIcon}
          selectedReportType={selectedReportType}
          editDisabled={editDisabled}
        />
      )}
      {isSupportedReportType(ReportType.DoughnutChart) && (
        <ReportTypeBadge
          reportType={ReportType.DoughnutChart}
          isReportChanged={isReportChanged}
          onSelected={onReportTypeClicked}
          tooltipText="Doughnut Chart Report"
          ReactTypeIcon={DoughnutChartIcon}
          selectedReportType={selectedReportType}
          editDisabled={editDisabled}
        />
      )}
      {isSupportedReportType(ReportType.Tile) && (
        <ReportTypeBadge
          reportType={ReportType.Tile}
          isReportChanged={isReportChanged}
          onSelected={onReportTypeClicked}
          tooltipText="Tile Report"
          ReactTypeIcon={TileIcon}
          selectedReportType={selectedReportType}
          editDisabled={editDisabled}
        />
      )}
    </Stack>
  );
};

export default ReportTypesContainer;
